<template>
  <div class="home">
    <section class="home-hero">
      <div class="hero-profile">
        <el-upload
          class="avatar-upload"
          :action="uploadApi"
          :headers="headers"
          :show-file-list="false"
          :multiple="false"
          :on-success="handleUploadSuccess"
          :limit="1">
          <img :src="avatar" class="avatar"/>
        </el-upload>
        <div class="hero-copy">
          <p class="date">{{ currentDateInfo }}</p>
          <h1>{{ staff.name || '员工' }}，欢迎回到人力资源工作台</h1>
          <p class="summary">{{ staff.deptName || '未分配部门' }} · 组织、人事、考勤、薪酬与文件管理统一入口</p>
        </div>
      </div>
      <div class="profile-facts">
        <div>
          <span>部门</span>
          <strong>{{ staff.deptName || '暂无' }}</strong>
        </div>
        <div>
          <span>生日</span>
          <strong>{{ staff.birthday || '暂无' }}</strong>
        </div>
        <div>
          <span>地址</span>
          <strong>{{ staff.address || '暂无' }}</strong>
        </div>
      </div>
    </section>

    <section class="metric-grid">
      <article v-for="(item,index) in countData" :key="index" class="metric-card">
        <div class="metric-icon" :style="{ color: item.color, background: item.bgColor }">
          <i :class="'el-icon-' + item.icon"/>
        </div>
        <div class="metric-content">
          <p>{{ item.name }}</p>
          <strong>{{ item.value }}</strong>
          <span>{{ item.desc }}</span>
        </div>
      </article>
    </section>

    <section class="workbench-grid">
      <div class="main-panel">
        <el-card class="chart-card">
          <div class="section-title">
            <div>
              <h2>组织结构概览</h2>
              <p>部门人数与员工入职趋势</p>
            </div>
          </div>
          <div class="chart-split">
            <div ref="staff" class="chart-box"></div>
            <div ref="department" class="chart-box"></div>
          </div>
        </el-card>

        <el-card class="chart-card">
          <div class="section-title">
            <div>
              <h2>社保缴费参考</h2>
              <p>部分城市企业缴费比例对比</p>
            </div>
          </div>
          <div ref="city" class="chart-box chart-wide"></div>
        </el-card>
      </div>

      <aside class="side-panel">
        <el-card class="quick-card">
          <div class="section-title compact">
            <div>
              <h2>快捷入口</h2>
              <p>高频业务快速到达</p>
            </div>
          </div>
          <div class="quick-list">
            <button
              v-for="item in availableQuickEntries"
              :key="item.name"
              type="button"
              class="quick-item"
              @click="goRoute(item.path)">
              <i :class="'el-icon-' + item.icon"/>
              <span>{{ item.name }}</span>
            </button>
            <div v-if="availableQuickEntries.length === 0" class="quick-empty">
              当前账号暂无可用快捷入口
            </div>
          </div>
        </el-card>

        <el-card class="alert-card">
          <div class="section-title compact">
            <div>
              <h2>待办与预警</h2>
              <p>基于当前统计数据生成</p>
            </div>
          </div>
          <div class="alert-list">
            <div v-for="item in alertItems" :key="item.title" class="alert-item" :class="item.level">
              <i :class="'el-icon-' + item.icon"/>
              <div>
                <strong>{{ item.title }}</strong>
                <span>{{ item.desc }}</span>
              </div>
            </div>
          </div>
        </el-card>

        <el-card class="calendar-card">
          <div class="section-title compact">
            <div>
              <h2>本月考勤</h2>
              <p>员工个人考勤状态</p>
            </div>
          </div>
          <el-calendar v-model="attendanceData.date">
            <template
              slot="dateCell"
              slot-scope="{date, data}">
              <span>{{ date.getDate() }}</span>
              <!-- 使用v-if避免在没有数据的时候显示解析-->
              <el-tag v-show="data.type==='current-month'" v-if="attendanceData.list[date.getDate() - 1]"
                      :type="attendanceData.list[date.getDate() - 1].tagType">
                {{ attendanceData.list[date.getDate() - 1].message }}
              </el-tag>
            </template>
          </el-calendar>
        </el-card>

        <el-card class="activity-card">
          <div class="section-title compact">
            <div>
              <h2>近期动态</h2>
              <p>系统数据同步状态</p>
            </div>
          </div>
          <div class="activity-list">
            <div v-for="item in activityItems" :key="item.title" class="activity-item">
              <span></span>
              <div>
                <strong>{{ item.title }}</strong>
                <p>{{ item.desc }}</p>
              </div>
            </div>
          </div>
        </el-card>
      </aside>
    </section>
  </div>
</template>
<script>
import * as echarts from 'echarts'
import { getAttendanceData, getCityData, getCountData, getDepartmentData, getStaffData } from '../../api/home'
import { getDownloadApi, getUploadApi } from '../../api/docs'
import { edit, getInfo } from '../../api/staff'
import { mapState } from 'vuex'

export default {
  name: 'Home',
  data () {
    return {
      dayOfWeek: ['星期天', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'],
      attendanceData: {
        date: '',
        list: []
      },
      countData: [
        {
          name: '员工总数',
          value: 0,
          icon: 'user',
          color: '#1d4ed8',
          bgColor: '#e8f0ff',
          desc: '组织在册人员'
        },
        {
          name: '状态正常',
          value: 0,
          icon: 'circle-check',
          color: '#047857',
          bgColor: '#e7f7ef',
          desc: '可正常参与排班'
        },
        {
          name: '状态异常',
          value: 0,
          icon: 'warning',
          color: '#b91c1c',
          bgColor: '#fdecec',
          desc: '需关注人员状态'
        },
        {
          name: '今日迟到',
          value: 0,
          icon: 'timer',
          color: '#b45309',
          bgColor: '#fff3dc',
          desc: '今日考勤异常'
        },
        {
          name: '今日早退',
          value: 0,
          icon: 'watch',
          color: '#7c3aed',
          bgColor: '#f1ebff',
          desc: '需核对离岗记录'
        },
        {
          name: '今日旷工',
          value: 0,
          icon: 'bell',
          color: '#be123c',
          bgColor: '#fff0f4',
          desc: '需优先处理'
        }
      ],
      quickEntries: [
        { name: '员工档案', icon: 'user', path: '/system/staff', codes: ['system', 'staff'] },
        { name: '部门组织', icon: 'office-building', path: '/system/department', codes: ['system', 'department'] },
        { name: '考勤管理', icon: 'date', path: '/attendance/performance', codes: ['attendance', 'performance'] },
        { name: '请假管理', icon: 'document-checked', path: '/attendance/leave', codes: ['attendance', 'leave'] },
        { name: '薪资管理', icon: 'money', path: '/money/salary', codes: ['money', 'salary'] },
        { name: '文件管理', icon: 'folder-opened', path: '/system/docs', codes: ['system', 'docs'] }
      ],
      staffOption: {
        title: {
          text: new Date().getFullYear() + '年员工入职情况',
          left: 'center',
          textStyle: {
            color: '#1f2937',
            fontSize: 15,
            fontWeight: 600
          }
        },
        grid: {
          top: 54,
          left: 40,
          right: 24,
          bottom: 36
        },
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c}人',
          textStyle: {
            fontWeight: 'bold'
          }
        },
        xAxis: {
          type: 'category',
          data: []
        },
        yAxis: {
          type: 'value',
          name: '人数'
        },
        series: [
          {
            data: [],
            type: 'line',
            smooth: true,
            symbolSize: 7,
            itemStyle: {
              color: '#1d4ed8'
            },
            lineStyle: {
              width: 3
            }
          },
          {
            data: [],
            type: 'bar',
            barWidth: 22,
            itemStyle: {
              color: '#8fb5f7',
              borderRadius: [4, 4, 0, 0]
            }
          }
        ]
      },
      cityOption: {
        title: {
          text: ''
        },
        legend: {
          top: 0,
          right: 10
        },
        grid: {
          top: 46,
          left: 54,
          right: 28,
          bottom: 36
        },
        tooltip: {
          trigger: 'item',
          formatter: function (params) {
            return '<a>' + params.name + '</a></a>' + '<br /><a>' + params.seriesName + ': ' + params.data[params.seriesIndex + 1] * 100 + '%</a>'
          },
          textStyle: {
            fontWeight: 'bold'
          }
        },
        dataset: {
          source: []
        },
        xAxis: { type: 'category' },
        yAxis: {
          type: 'value',
          name: '百分比',
          axisLabel: {
            formatter: function (val) {
              return val * 100 + '%'
            }
          }
        },
        series: [
          { type: 'line', smooth: true },
          { type: 'line', smooth: true },
          { type: 'line', smooth: true },
          { type: 'line', smooth: true }
        ]
      },
      departmentOption: {
        title: {
          text: '员工分布情况',
          left: 'center',
          textStyle: {
            color: '#1f2937',
            fontSize: 15,
            fontWeight: 600
          }
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          top: 28
        },
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c}人',
          textStyle: {
            fontWeight: 'bold'
          }
        },
        toolbox: {
          show: true,
          feature: {
            mark: { show: true },
            dataView: { show: true, readOnly: false },
            restore: { show: true },
            saveAsImage: { show: true }
          }
        },
        series: [
          {
            name: 'Nightingale Chart',
            type: 'pie',
            radius: ['34%', '62%'],
            center: ['50%', '55%'],
            label: { // 饼图图形上的文本标签
              normal: {
                show: true,
                position: 'inner',
                textStyle: {
                  fontWeight: 'bold'
                },
                formatter: '{d}%'
              }
            },
            itemStyle: {
              borderRadius: 3
            },
            data: []
          }
        ]
      }
    }
  },
  computed: {
    ...mapState('staff', ['staff']),
    ...mapState('token', ['token']),
    ...mapState('menu', ['menuList']),
    headers () {
      return { token: this.token }
    },
    uploadApi () {
      return getUploadApi()
    },
    downloadApi () {
      return getDownloadApi()
    },
    currentDateInfo () {
      const date = new Date()
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const currentDate = date.getDate()
      const day = date.getDay()
      return year + '年' + month + '月' + currentDate + '日' + ' ' + this.dayOfWeek[day]
    },
    avatar () {
      return this.staff.avatar ? this.downloadApi + this.staff.avatar : require('../../assets/images/avatar.png')
    },
    availableQuickEntries () {
      const codes = []
      const collect = (list) => {
        list.forEach(item => {
          codes.push(item.code)
          if (item.children && item.children.length > 0) {
            collect(item.children)
          }
        })
      }
      collect(this.menuList || [])
      return this.quickEntries.filter(item => item.codes.every(code => codes.includes(code)))
    },
    alertItems () {
      const abnormal = Number(this.countData[2].value) || 0
      const late = Number(this.countData[3].value) || 0
      const leaveEarly = Number(this.countData[4].value) || 0
      const absenteeism = Number(this.countData[5].value) || 0
      const items = []
      if (absenteeism > 0) {
        items.push({ title: '旷工预警', desc: absenteeism + ' 条旷工记录待核验', icon: 'warning-outline', level: 'danger' })
      }
      if (late + leaveEarly > 0) {
        items.push({ title: '考勤异常', desc: late + ' 次迟到，' + leaveEarly + ' 次早退', icon: 'time', level: 'warning' })
      }
      if (abnormal > 0) {
        items.push({ title: '员工状态', desc: abnormal + ' 名员工状态需关注', icon: 'user', level: 'warning' })
      }
      if (items.length === 0) {
        items.push({ title: '运行平稳', desc: '今日暂无高优先级人事预警', icon: 'circle-check', level: 'normal' })
      }
      return items
    },
    activityItems () {
      return [
        { title: '组织数据已同步', desc: '当前员工总数 ' + this.countData[0].value + ' 人，正常状态 ' + this.countData[1].value + ' 人。' },
        { title: '考勤看板已更新', desc: '今日迟到、早退、旷工数据已汇总到工作台。' },
        { title: '个人考勤日历', desc: '可按月份切换查看当前员工考勤状态。' }
      ]
    }
  },
  watch: {
    'attendanceData.date':
      function () {
        this.getStaffAttendance()
      }
  },
  methods: {
    loading () {
      getInfo(this.staff.id).then(response => {
        if (response.code === 200) {
          this.$store.commit('staff/SET_STAFF', response.data)
        } else {
          this.$message.error('获取数据失败！')
        }
      })

      getStaffData().then(response => {
        if (response.code === 200) {
          const quarters = ['一季度', '二季度', '三季度', '四季度']
          this.staffOption.xAxis.data = quarters
          this.staffOption.series.forEach(item => {
            item.data = response.data
          })
          const staffChart = echarts.init(this.$refs.staff)
          staffChart.setOption(this.staffOption)
        } else {
          this.$message.error('获取数据失败！')
        }
      })

      // 获取统计数据
      getCountData().then(response => {
        if (response.code === 200) {
          this.countData[0].value = response.data.totalNum
          this.countData[1].value = response.data.normalNum
          this.countData[2].value = response.data.totalNum - response.data.normalNum
          this.countData[3].value = response.data.lateNum
          this.countData[4].value = response.data.leaveEarlyNum
          this.countData[5].value = response.data.absenteeismNum
        } else {
          this.$message.error('获取数据失败！')
        }
      })

      // 获取城市设社保数据
      getCityData().then(response => {
        if (response.code === 200) {
          const cityData = response.data.map(item => [item.name, item.comPensionRate, item.comMedicalRate, item.comUnemploymentRate, item.comMaternityRate])
          cityData.unshift(['product', '养老保险', '医疗保险', '失业保险', '生育保险'])
          this.cityOption.dataset.source = cityData
          const cityChart = echarts.init(this.$refs.city)
          cityChart.setOption(this.cityOption)
        } else {
          this.$message.error('获取数据失败！')
        }
      })

      // 获取当前月员工的考勤数据
      getAttendanceData({ id: this.staff.id }).then(response => {
        if (response.code === 200) {
          this.attendanceData.list = response.data
        } else {
          this.$message.error('获取数据失败！')
        }
      })

      getDepartmentData().then(response => {
        if (response.code === 200) {
          this.departmentOption.series[0].data = response.data
          const departmentChart = echarts.init(this.$refs.department)
          departmentChart.setOption(this.departmentOption)
        } else {
          this.$message.error('获取数据失败！')
        }
      })
    },
    // 获取指定月份员工的考勤数据
    getStaffAttendance () {
      const d = this.attendanceData.date
      let month = ''
      if (d.getMonth() < 9) {
        month = d.getFullYear() + '0' + (d.getMonth() + 1)
      } else {
        month = d.getFullYear() + '' + (d.getMonth() + 1)
      }
      getAttendanceData({ id: this.staff.id, month: month }).then(response => {
        if (response.code === 200) {
          this.attendanceData.list = response.data
        } else {
          this.$message.error('获取数据失败！')
        }
      })
    },
    handleUploadSuccess (res) {
      if (res.code === 200) {
        // 修改头像
        edit({ id: this.staff.id, avatar: res.data.name }).then(response => {
          if (response.code === 200) {
            this.$message.success('修改头像成功！')
            this.$store.commit('staff/SET_AVATAR', res.data.name)
          } else {
            this.$message.error('修改头像失败！')
          }
        })
      } else {
        this.$message.error('文件上传失败！')
      }
    },
    goRoute (path) {
      this.$router.push({ path })
    }
  },
  created () {
    this.loading()
  }
}
</script>
